// @ts-check
import classNames from 'classnames';
import { graphql } from 'gatsby';
import React from 'react';
import CustomDebitCardDesignIframe from '../components/CustomDebitCardDesignIframe';
import LinkButton from '../components/LinkButton';
import PersonnelInfo from '../components/PersonnelInfo';
import { withReferencePath } from '../utils/ReferencePaths';
import AccentedTextSection from './AccentedTextSection';
import CollageBlock from './CollageBlock';
import CustomSection from './CustomSection';
import FAQSection from './FAQSection';
import FileList from './FileList';
import ImageButtonSection, { ImageButtonSection_REFERENCES } from './ImageButtonSection';
import ImageSection from './ImageSection';
import LedgerSection from './LedgerSection';
import ListSection from './ListSection';
import TableSection from './TableSection';
import TextSection from './TextSection';

import './CopyBlock.scss';

export const query = graphql`
	fragment CopyBlockFragment on Ozk_content_blockModular_blocks {
		copy {
			text_align
			column_option
			content {
				...AccentedTextFragment
				...ButtonFragment
				...CollageFragment
				...FAQSectionFragment
				...ImageButtonFragment
				...ImageSectionFragment
				...LedgerFragment
				...ListSectionFragment
				...FileListFragment
				...PersonnelInfoFragment
				...TableFragment
				...TextSectionFragment
				...CustomDebitCardFragment
			}
		}
	}
`;

const [, withPaths] = withReferencePath('modular_blocks.copy.content');
export const CopyBlock_REFERENCES = [...withPaths(ImageButtonSection_REFERENCES)];

/**
 * @typedef {'none' | 'hidden' | 'number' | 'letter' | 'dense' | 'gray-card'} ModularBlockColumnHeadingOption
 
 * @typedef ModularBlockCopyContent
 * @property {MainContentOptions[]} content
 * @property {'left' | 'center' | 'right'} [text_align="center"]
 * @property {ModularBlockColumnHeadingOption} column_option
 *
 * @typedef ModularBlockCopy
 * @property {ModularBlockCopyContent} copy
 *
 * @typedef MainContentOptions
 * @property {import('./AccentedTextSection').AccentedTextOptions} [accented_text]
 * @property {import('../components/LinkButton').ButtonOption} [button]
 * @property {import('./CollageBlock').CollageBlockOptions} [collage]
 * @property {import('./CustomSection').CustomOptions} [custom]
 * @property {import('./FAQSection').FAQOptions} [faq]
 * @property {import('./ImageSection').ImageSectionOptions} [image]
 * @property {import('./ImageButtonSection').ImageButtonSectionOptions} [image_button]
 * @property {import('./LedgerSection').LedgerOptions} [ledger]
 * @property {import('./ListSection').ListOptions} [list]
 * @property {import('./FileList').FileListOptions} [file_list]
 * @property {import('../components/PersonnelInfo').PersonnelInfoOptions} [personnel_info]
 * @property {import('./TableSection').TableOptions} [table]
 * @property {import('./TextSection').TextOptions} [text]
 * @property {import('../components/CustomDebitCardDesignIframe').CustomDebitCardDesignIframeProps} [custom_debit_card_design_iframe]
 */

/** @type {React.FC<{ options: ModularBlockCopy }>} */
const CopyBlock = ({ options }) => {
	if (!options?.copy?.content?.length) {
		return null;
	}

	return (
		<div className={classNames('copy-block', 'block-text-align-' + options?.copy?.text_align)}>
			{options.copy.content.map((opt, i) => (
				<React.Fragment key={i}>
					{!!opt.accented_text && <AccentedTextSection options={opt.accented_text} />}
					{!!opt.button && <LinkButton options={opt.button} />}
					{!!opt.collage && <CollageBlock options={opt.collage} />}
					{!!opt.custom && <CustomSection options={opt.custom} />}
					{!!opt.faq && <FAQSection options={opt.faq} />}
					{!!opt.image && <ImageSection options={opt.image} />}
					{!!opt.image_button && <ImageButtonSection options={opt.image_button} />}
					{!!opt.ledger && <LedgerSection options={opt.ledger} />}
					{!!opt.list && <ListSection options={opt.list} />}
					{!!opt.file_list && <FileList options={opt.file_list} />}
					{!!opt.personnel_info && <PersonnelInfo options={opt.personnel_info} />}
					{!!opt.table && <TableSection options={opt.table} />}
					{!!opt.text && <TextSection options={opt.text} />}
					{!!opt.custom_debit_card_design_iframe && (
						<CustomDebitCardDesignIframe heading={opt.custom_debit_card_design_iframe.heading} />
					)}
				</React.Fragment>
			))}
		</div>
	);
};
export default CopyBlock;
